import React from 'react';
import { useTheme } from '@mui/material/styles';
import { toast } from 'sonner';
import AryIconButton from 'framework/desktop/AryIconButton';
import { Box } from '@mui/material';
import { Close, Info, Warning } from '@mui/icons-material';
import AryToastTypes from '../constants/AryToastTypes';
import useSystemSettingsContext from './useSystemSettingsContext';
import arySystemSettings from '../constants/arySystemSettings';

const getToastIcon = (type, theme) => {
  switch (type) {
    case 'error':
      return <Info sx={{ color: theme.ticket.toast.errorIconColor }} />;
    case 'info':
      return <Info sx={{ color: theme.ticket.toast.infoIconColor }} />;
    case 'warning':
      return <Warning sx={{ color: theme.ticket.toast.warnIconColor }} />;
    default:
      return null;
  }
};

const useShowToast = () => {
  const theme = useTheme();
  const { systemSettings } = useSystemSettingsContext();

  const toastStyles = {
    error: {
      backgroundColor: theme.ticket.toast.backgroundColor,
      color: theme.ticket.toast.text,
      border: 'none'
    },
    warning: {
      backgroundColor: theme.ticket.toast.backgroundColor,
      color: theme.ticket.toast.text,
      border: 'none'
    },
    info: {
      backgroundColor: theme.ticket.toast.backgroundColor,
      color: theme.ticket.toast.text,
      border: 'none'
    }
  };

  const showToast = (
    message,
    type = 'info',
    duration = 3000,
    customButton = null
  ) => {
    const toastId = Math.random().toString(36).substring(2, 9);
    const calculatedDuration = Math.max(duration, message.length * 100);

    const renderToast = () => (
      <Box
        role="button"
        tabIndex={0}
        onClick={() => toast.dismiss(toastId)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            toast.dismiss(toastId);
          }
        }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          width: '100%',
          height: '100%',
          minWidth: '20rem',
          cursor: 'pointer'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            justifyContent: 'flex-start',
            flexDirection:
              systemSettings?.[arySystemSettings.systemLanguage] === 'en'
                ? 'row-reverse'
                : 'row'
          }}
        >
          <AryIconButton
            Icon={Close}
            iconColor={theme.ticket.toast.closeIconColor}
            onClick={(e) => {
              e.stopPropagation();
              toast.dismiss(toastId);
            }}
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              minHeight: '2rem',
              gap: '0.5rem',
              flexDirection:
                systemSettings?.[arySystemSettings.systemLanguage] === 'en'
                  ? 'row-reverse'
                  : 'row',
              justifyContent: 'flex-end',
              width: '100%'
            }}
          >
            <span
              style={{
                color: theme.ticket.toast.text,
                textAlign:
                  systemSettings?.[arySystemSettings.systemLanguage] === 'en'
                    ? 'left'
                    : 'right'
              }}
            >
              {message}
            </span>
            {getToastIcon(type, theme)}
          </Box>
        </Box>
        {customButton && <Box sx={{ alignSelf: 'end' }}>{customButton}</Box>}
      </Box>
    );

    switch (type) {
      case AryToastTypes.ERROR:
        toast.error(renderToast, {
          duration: calculatedDuration,
          icon: false,
          style: toastStyles.error,
          id: toastId
        });
        break;
      case AryToastTypes.WARNING:
        toast.warning(renderToast, {
          duration: calculatedDuration,
          icon: false,
          style: toastStyles.warning,
          id: toastId
        });
        break;
      case AryToastTypes.INFO:
        toast(renderToast, {
          duration: calculatedDuration,
          icon: false,
          style: toastStyles.info,
          id: toastId
        });
        break;
      default:
        toast(renderToast, {
          duration: calculatedDuration,
          icon: false,
          style: toastStyles.info,
          id: toastId
        });
    }
  };

  return { showToast };
};

export default useShowToast;
